import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// All brand assets self-hosted from /public/brand/ so they deploy with the bundle
// (no cross-environment object-storage dependency)
export const CK_MONOGRAM = "/ck-192.png";
// Full Coastal Kraken Baseball badge — the hero logo (transparent)
export const KRAKEN_FULL_LOGO = "/brand/kraken-full-logo.png";
// Standalone octopus mascot (transparent)
export const KRAKEN_OCTOPUS = "/brand/kraken-octopus.png";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/coaches", label: "Coaches" },
  { to: "/roster", label: "Roster" },
  { to: "/schedule", label: "Schedule" },
  { to: "/scores", label: "Scores" },
  { to: "/camps", label: "Camps" },
  { to: "/tryouts", label: "Tryouts" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/merch", label: "Shop" },
  { to: "/gallery", label: "Gallery" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export function KrakenLogo({ size = 40 }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0" data-testid="kraken-logo">
      <img src={CK_MONOGRAM} alt="Coastal Kraken" style={{ width: size, height: size }} className="object-contain drop-shadow-[0_0_12px_rgba(0,229,255,0.4)] shrink-0" />
      <div className="leading-tight whitespace-nowrap min-w-0">
        <div className="font-display text-xs sm:text-base font-bold tracking-widest text-white">
          <span className="sm:hidden">COASTAL KRAKEN</span>
          <span className="hidden sm:inline">COASTAL KRAKEN BASEBALL</span>
        </div>
      </div>
    </div>
  );
}

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col text-white">
      <header className="nav-glass sticky top-0 z-50" data-testid="public-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" data-testid="nav-home"><KrakenLogo /></Link>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
          <nav className="hidden lg:flex items-center gap-0.5 text-sm">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-link px-2.5 py-2 rounded-md transition tracking-wide ${isActive ? "active text-[#00E5FF]" : "text-slate-300 hover:text-white"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user && user !== false ? (
              <Link to="/portal" className="btn-primary ml-2 whitespace-nowrap" data-testid="nav-portal">Portal</Link>
            ) : (
              <Link to="/login" className="btn-primary ml-2 whitespace-nowrap" data-testid="nav-login">Sign In</Link>
            )}
          </nav>
        </div>
        {open && (
          <div className="lg:hidden border-t border-white/10 bg-[#050A15] px-4 py-3 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md ${isActive ? "text-[#00E5FF]" : "text-slate-300"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              {user && user !== false ? (
                <Link to="/portal" onClick={() => setOpen(false)} className="btn-primary mt-2 justify-center">Portal</Link>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="btn-primary mt-2 justify-center">Sign In</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1" key={location.pathname}>{children}</main>

      <footer className="border-t border-white/10 bg-[#050A15] mt-20" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <KrakenLogo />
            <p className="text-slate-400 text-sm mt-4 leading-relaxed">
              Anchored in Faith. Relentless in Competition.
            </p>
          </div>
          <div>
            <div className="eyebrow mb-3">Team</div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li><Link to="/about">About</Link></li>
              <li><Link to="/coaches">Coaches</Link></li>
              <li><Link to="/roster">Roster</Link></li>
              <li><Link to="/schedule">Schedule</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-3">Get Involved</div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li><Link to="/tryouts">Tryouts</Link></li>
              <li><Link to="/camps">Camps & Clinics</Link></li>
              <li><Link to="/sponsors">Sponsorship</Link></li>
              <li><Link to="/donate">Donate</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-3">Connect</div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li><Link to="/contact">Contact Us</Link></li>
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/gallery">Media Gallery</Link></li>
            </ul>
          </div>
        </div>
        <div className="text-center text-xs text-slate-500 py-6 border-t border-white/5">
          © {new Date().getFullYear()} Coastal Kraken Baseball · Anchored in Faith. Relentless in Competition.
        </div>
      </footer>
    </div>
  );
}
